import {
    fetchRemoteConfig,
    initialFetchRemoteConfig,
    restoreItidaCustomSignal
} from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';

/** Locale-shaped payload from `WEB_SUBSCRIPTION_OFFERINGS_WEB_PAGE` RC JSON. */
export type SubscriptionRcPlansPayload = {
    packages: unknown[];
    FAQs?: Record<string, string>;
    [key: string]: unknown;
};

/**
 * Per-language-code in-flight deduplicator.
 * SubscriptionAvailabilityContext (layout) and SubscriptionPlansClient (page)
 * both call this on mount. Without deduplication, two concurrent
 * initialFetchRemoteConfig(true) calls race on the Firebase RC singleton and
 * can activate a throttled/stale cached RC response that contains packages
 * even when the current server config has none.
 */
const _inflightChecks = new Map<
    string,
    Promise<SubscriptionRcPlansPayload | null>
>();

async function _doLoadWebSubscriptionPlansOrNull(
    languageCode: string
): Promise<SubscriptionRcPlansPayload | null> {
    await restoreItidaCustomSignal();
    await initialFetchRemoteConfig(true);

    const enabled = (await fetchRemoteConfig(
        RemoteConfigKeys.WEB_SUBSCRIPTION_OFFERING_WEB_PAGE_SWITCH,
        'boolean',
        true
    )) as boolean;

    if (!enabled) {
        return null;
    }

    const raw = await fetchRemoteConfig(
        RemoteConfigKeys.WEB_SUBSCRIPTION_OFFERINGS_WEB_PAGE,
        'object',
        true
    );

    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }

    const plans = (raw as Record<string, SubscriptionRcPlansPayload>)[
        languageCode
    ];
    if (!plans || typeof plans !== 'object') {
        return null;
    }

    const packages = plans.packages;
    if (!Array.isArray(packages) || packages.length === 0) {
        return null;
    }

    return {
        ...plans,
        FAQs: plans.FAQs ?? {}
    };
}

/**
 * Loads locale plans from RC when the kill-switch is on and packages exist.
 * Concurrent calls with the same languageCode share one in-flight check so
 * only a single fetchAndActivate runs — preventing the stale-RC race where
 * two concurrent Firebase calls activate different (cached) snapshots.
 */
export function loadWebSubscriptionPlansOrNull(
    languageCode: string
): Promise<SubscriptionRcPlansPayload | null> {
    const existing = _inflightChecks.get(languageCode);
    if (existing) return existing;

    const promise = _doLoadWebSubscriptionPlansOrNull(languageCode).finally(() => {
        _inflightChecks.delete(languageCode);
    });
    _inflightChecks.set(languageCode, promise);
    return promise;
}

/**
 * Web subscriptions are available only when the RC kill-switch is on and the
 * resolved offerings JSON includes a non-empty packages array for this locale.
 */
export async function getWebSubscriptionsAvailability(
    languageCode: string
): Promise<boolean> {
    const plans = await loadWebSubscriptionPlansOrNull(languageCode);
    return plans !== null;
}
